import React, { useMemo } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTheme } from "@mui/material/styles";
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  CHANNEL_BREAKDOWN,
  MAX_LEGEND_SEGMENTS,
  OTHER_LABEL,
  buildStackedRows,
  collectSegmentNames,
  limitSegmentNames,
  segmentColor,
  segmentDataKey,
} from "./salesHistoryBreakdown";
const CHART_MARGIN = { top: 8, right: 8, left: 0, bottom: 0 };

const LegendSwatch = ({ color, label }) => (
  <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.7 }}>
    <Box
      component="span"
      sx={{
        width: 9,
        height: 9,
        borderRadius: "2px",
        backgroundColor: color,
        display: "inline-block",
      }}
    />
    <Typography component="span" sx={{ fontSize: 11.5, color: "text.secondary" }}>
      {label}
    </Typography>
  </Box>
);

const formatNumber = (value, digits = 0) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "—";
  }
  return Number(value).toLocaleString(undefined, {
    maximumFractionDigits: digits,
    minimumFractionDigits: 0,
  });
};

const formatDayTick = (dayKey) => String(dayKey || "").slice(8);

const formatDayLong = (dayKey) => {
  if (!dayKey) return "";
  const d = new Date(`${dayKey}T00:00:00.000Z`);
  if (Number.isNaN(d.getTime())) return dayKey;
  return d.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
};

const formatMonthLong = (month) => {
  if (!month) return "";
  const d = new Date(`${month}-01T00:00:00.000Z`);
  if (Number.isNaN(d.getTime())) return month;
  return d.toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
};

/**
 * Units is the only plotted series. Orders and the order-value ratio are
 * context for a single day, so they belong in the tooltip rather than as
 * competing lines on their own scales.
 */
export const DailyTip = ({ active, payload, label, stacked, hideOrders }) => {
  if (!active || !payload?.length) return null;
  const point = payload[0]?.payload || {};
  return (
    <Paper
      elevation={3}
      sx={{
        px: 1.5,
        py: 1.1,
        borderRadius: 1,
        bgcolor: "rgba(33, 33, 33, 0.94)",
        color: "#fff",
        minWidth: 210,
      }}
    >
      <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 0.5, color: "#fff" }}>
        {formatDayLong(label)}
      </Typography>
      {point.no_evidence ? (
        <Typography sx={{ fontSize: 11, color: "#ffe082" }}>
          No sales evidence yet on this day. Not treated as zero.
        </Typography>
      ) : (
        <>
          <Typography sx={{ fontSize: 11, color: "#fff" }}>
            Units sold: {formatNumber(point.units_sold)}
          </Typography>
          {!hideOrders && (
            <Typography sx={{ fontSize: 11, color: "#fff" }}>
              Orders with this SKU: {formatNumber(point.orders)}
            </Typography>
          )}
          {stacked &&
            (point.segments || []).map((part) => (
              <Box
                key={part.name}
                sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.35 }}
              >
                <Box
                  component="span"
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "2px",
                    backgroundColor: part.color,
                    flexShrink: 0,
                  }}
                />
                <Typography sx={{ fontSize: 11, color: "#e0e0e0" }}>
                  {part.name}: {formatNumber(part.units_sold)}
                </Typography>
              </Box>
            ))}
          {!hideOrders &&
            point.order_value_ratio !== null &&
            point.order_value_ratio !== undefined && (
              <Typography sx={{ fontSize: 10.5, color: "#b0bec5", mt: 0.5 }}>
                Avg order-value ratio: {point.order_value_ratio} (order totals
                include tax and shipping)
              </Typography>
            )}
        </>
      )}
    </Paper>
  );
};

const SalesHistoryDailyAnalysis = ({
  month,
  loading,
  error,
  analysis,
  sourceOptions = [],
  dimension = CHANNEL_BREAKDOWN,
  segmentOptions = null,
  colorOrder = null,
  hideOrders = false,
  onClose,
  ui,
}) => {
  const theme = useTheme();
  const unitsColor = theme.palette.error.main;
  const days = useMemo(() => analysis?.days || [], [analysis]);

  // Same ordering, palette and long-tail handling as the monthly chart.
  const options = segmentOptions || sourceOptions;
  const present = useMemo(
    () => collectSegmentNames(days, dimension, options),
    [days, dimension, options],
  );
  const stackSources = useMemo(
    () => limitSegmentNames(present, days, dimension),
    [present, days, dimension],
  );

  const stacked = present.length > 0;
  // Dense colour index: position among the segments actually drawn, so hues
  // stay maximally separated and can never wrap onto a duplicate. Shared with
  // the sibling chart when the caller supplies one.
  const colorKeys = colorOrder || stackSources;
  const hasOther = stackSources.includes(OTHER_LABEL);

  const chartData = useMemo(
    () =>
      stacked
        ? buildStackedRows({
            rows: days,
            dimension,
            names: stackSources,
            options: colorKeys,
            isBlank: (row) => row.no_evidence,
          })
        : days,
    [days, stackSources, stacked, colorKeys, dimension],
  );

  return (
    <Box sx={{ ...ui.card, px: 2, pt: 1.75, pb: 1.5, mb: 2.5 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          mb: 1.25,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{ fontSize: 14, fontWeight: 700, color: ui.colors.textPrimary }}
          >
            Daily Analysis — {formatMonthLong(month)}
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: ui.colors.textSecondary }}>
            Units sold per day. Click another month to switch.
          </Typography>
        </Box>
        <IconButton
          aria-label="Close daily analysis"
          onClick={onClose}
          size="small"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={26} />
        </Box>
      )}

      {!loading && error && (
        <Alert severity="error" sx={{ borderRadius: "10px" }}>
          {error}
        </Alert>
      )}

      {!loading && !error && analysis && (
        <>
          <Box
            sx={{
              display: "flex",
              gap: 1.75,
              alignItems: "center",
              flexWrap: "wrap",
              mb: 0.75,
            }}
          >
            {stacked ? (
              stackSources.map((source) => (
                <LegendSwatch
                  key={source}
                  color={segmentColor(source, colorKeys)}
                  label={source}
                />
              ))
            ) : (
              <LegendSwatch color={unitsColor} label="Units sold" />
            )}
          </Box>
          <Box sx={{ height: { xs: 230, sm: 280 } }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} margin={CHART_MARGIN}>
              <CartesianGrid
                strokeDasharray="3 3"
                vertical={false}
                stroke={theme.palette.divider}
              />
              <XAxis
                dataKey="day"
                tickFormatter={formatDayTick}
                minTickGap={10}
                tick={{ fontSize: 10.5, fontFamily: "Poppins" }}
              />
              <YAxis
                tickFormatter={(value) => formatNumber(value)}
                width={46}
                tick={{ fontSize: 10.5, fontFamily: "Poppins" }}
              />
              <RechartsTooltip content={<DailyTip stacked={stacked} hideOrders={hideOrders} />} />
              {stacked ? (
                stackSources.map((source, index) => (
                  <Bar
                    key={source}
                    dataKey={segmentDataKey(source)}
                    name={source}
                    stackId="units"
                    fill={segmentColor(source, colorKeys)}
                    maxBarSize={18}
                    radius={
                      index === stackSources.length - 1 ? [2, 2, 0, 0] : undefined
                    }
                  >
                    {chartData.map((row) => (
                      <Cell
                        key={row.day}
                        fill={segmentColor(source, colorKeys)}
                        fillOpacity={0.9}
                      />
                    ))}
                  </Bar>
                ))
              ) : (
                <Bar
                  dataKey="units_sold"
                  name="Units sold"
                  fill={unitsColor}
                  radius={[2, 2, 0, 0]}
                  maxBarSize={18}
                  fillOpacity={0.85}
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
          </Box>
          {hasOther && (
            <Typography
              sx={{ fontSize: 11, color: ui.colors.textSecondary, mt: 0.5 }}
            >
              Smaller {dimension.noun} beyond the top {MAX_LEGEND_SEGMENTS} are
              grouped as “{OTHER_LABEL}”.
            </Typography>
          )}
        </>
      )}
    </Box>
  );
};

export default SalesHistoryDailyAnalysis;
